import { CatalogProduct, USDAProductStatusTypeData } from '../../api';
import {
  CreateRecipeIngredientCategoryRequest,
  DeleteRecipeIngredientCategoryRequest,
  RecipeIngredientCategory,
  UpdateRecipeIngredientCategoryRequest,
} from '../../api/models/recipe-ingredient-category.models';
import {
  CreateRecipeIngredientHeaderRequest,
  DeleteRecipeIngredientHeaderRequest,
  RecipeIngredientHeader,
  UpdateRecipeIngredientHeaderRequest,
} from '../../api/models/recipe-ingredient-header.models';
import {
  CreateRecipeIngredientProductRequest,
  CreateRecipeIngredientProductsRequest,
  DeleteRecipeIngredientProductRequest,
  RecipeIngredientProduct,
  SequenceRecipeIngredientProductsRequest,
  UpdateRecipeIngredientProductRequest,
} from '../../api/models/recipe-ingredient-product.models';
import RecipeDetailService from '../../api/services/recipe-detail.service';
import RecipeIngredientCategoryService from '../../api/services/recipe-ingredient-category.service';
import RecipeIngredientDiaryService from '../../api/services/recipe-ingredient-diary.service';
import RecipeIngredientHeaderService from '../../api/services/recipe-ingredient-header.service';
import RecipeIngredientProductService from '../../api/services/recipe-ingredient-product.service';
import { addDays, generateStaticId, normalizeProductKey, truncateTime } from '../../helpers';
import { useAppInsightsLogger } from '../../logging';
import {
  NotificationDisplayType,
  NotificationKeys,
  NotificationType,
  Resolution,
  UserActivityAction,
  UserActivityActionSummary,
  UserActivityPageName,
} from '../../models';
import {
  globalSlice,
  logUserActivity,
  selectNextDeliveryDate,
  upsertAppNotification,
  upsertAppNotificationByKey,
} from '../common';
import { getUSDAProductStatusTypes } from '../orders';
import { AppDispatch, AppThunk, RootState } from '../store';
import { ingredientsSlice } from './ingredients.slice';

const appInsightsLogger = useAppInsightsLogger();
const recipeIngredientHeaderService = RecipeIngredientHeaderService.getInstance();
const recipeIngredientCategoryService = RecipeIngredientCategoryService.getInstance();
const recipeIngredientDiaryService = RecipeIngredientDiaryService.getInstance();
const recipeIngredientProductService = RecipeIngredientProductService.getInstance();
const recipeDetailService = RecipeDetailService.getInstance();

// Ingredients
export const getRecipeIngredientHeader =
  (ingredientId: string, customerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientsLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientsLoading(true));
      }

      const { data } = await recipeIngredientHeaderService.getRecipeIngredientHeader(ingredientId, customerId);

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredient(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientsLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const getRecipeIngredientHeaders =
  (customerId: string, isPreload?: boolean): AppThunk =>
  async (dispatch, getState) => {
    if (getState().ingredients.gridParameters?.customerId === customerId) {
      return; // no-op
    }

    if (!isPreload) {
      dispatch(ingredientsSlice.actions.setGridParameters({ customerId }));
    }

    try {
      if (!getState().ingredients.isIngredientsLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientsLoading(true));
      }

      const { data } = await recipeIngredientHeaderService.getRecipeIngredientHeaders(customerId);

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredients(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientsLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const createRecipeIngredientHeader =
  (
    request: CreateRecipeIngredientHeaderRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientHeader | undefined>> =>
  async (dispatch): Promise<RecipeIngredientHeader | undefined> => {
    let result;
    try {
      const { data } = await recipeIngredientHeaderService.createRecipeIngredientHeader(request);
      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredient(data.ResultObject));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CreateIngredient,
            pageName: UserActivityPageName.Ingredients,
            actionSummary: UserActivityActionSummary.CreateIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const updateRecipeIngredientHeader =
  (
    request: UpdateRecipeIngredientHeaderRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientHeader | undefined>> =>
  async (dispatch): Promise<RecipeIngredientHeader | undefined> => {
    let result;
    try {
      const { data } = await recipeIngredientHeaderService.updateRecipeIngredientHeader(request);
      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredient(data.ResultObject));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.Ingredients,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const deleteRecipeIngredientHeader =
  (request: DeleteRecipeIngredientHeaderRequest, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch): Promise<boolean> => {
    let result = false;
    try {
      const { data } = await recipeIngredientHeaderService.deleteRecipeIngredientHeader(request);

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.removeIngredient(request.RecipeIngredientHeaderId));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        result = true;
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteIngredient,
            pageName: UserActivityPageName.Ingredients,
            actionSummary: UserActivityActionSummary.DeleteIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

// Ingredient categories
export const getRecipeIngredientCategories =
  (customerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientCategoriesLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientCategoriesLoading(true));
      }

      const { data } = await recipeIngredientCategoryService.getRecipeIngredientCategorys(customerId);

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredientCategories(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientCategoriesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const createRecipeIngredientCategory =
  (
    request: CreateRecipeIngredientCategoryRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientCategory | undefined>> =>
  async (dispatch) => {
    let result;

    try {
      const { data } = await recipeIngredientCategoryService.createRecipeIngredientCategory(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been created'));
        dispatch(ingredientsSlice.actions.setIngredientCategory(data.ResultObject));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CreateIngredientCategory,
            pageName: UserActivityPageName.IngredientCategories,
            actionSummary: UserActivityActionSummary.CreateIngredientCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const updateRecipeIngredientCategory =
  (
    request: UpdateRecipeIngredientCategoryRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientCategory | undefined>> =>
  async (dispatch) => {
    let result;
    try {
      const { data } = await recipeIngredientCategoryService.updateRecipeIngredientCategory(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been updated'));
        dispatch(ingredientsSlice.actions.setIngredientCategory(data.ResultObject));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredientCategory,
            pageName: UserActivityPageName.IngredientCategories,
            actionSummary: UserActivityActionSummary.EditIngredientCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const deleteRecipeIngredientCategory =
  (request: DeleteRecipeIngredientCategoryRequest, resolution: Resolution): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const { data } = await recipeIngredientCategoryService.deleteRecipeIngredientCategory(request);

      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Category has been deleted'));
        dispatch(ingredientsSlice.actions.removeIngredientCategory(request.RecipeIngredientCategoryIds[0]));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteIngredientCategory,
            pageName: UserActivityPageName.IngredientCategories,
            actionSummary: UserActivityActionSummary.DeleteIngredientCategory,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Ingredient products
export const getIngredientProductPrimary =
  (recipeIngredientHeaderId: string, customerId: string): AppThunk<Promise<RecipeIngredientProduct | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    try {
      if (getState().ingredients.isIngredientProductsLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(true));
      }

      const { data } = await recipeIngredientProductService.getRecipeIngredientProductPrimary(
        recipeIngredientHeaderId,
        customerId
      );

      if (data.IsSuccess) {
        dispatch(
          ingredientsSlice.actions.setIngredientProduct({ product: data.ResultObject, isCustomerPrimary: false })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
      dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const getIngredientProduct =
  (recipeIngredientProductId: string, customerId: string): AppThunk<Promise<RecipeIngredientProduct | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    try {
      if (getState().ingredients.isIngredientProductsLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(true));
      }
      const deliveryDate = selectNextDeliveryDate(getState());

      const { data } = await recipeIngredientProductService.getRecipeIngredientProduct(
        recipeIngredientProductId,
        customerId,
        deliveryDate
      );

      if (data.IsSuccess) {
        dispatch(
          ingredientsSlice.actions.setIngredientProduct({ product: data.ResultObject, isCustomerPrimary: false })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
      dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const getIngredientProducts =
  (recipeIngredientHeaderId: string, customerId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientProductsLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(true));
      }
      const deliveryDate = selectNextDeliveryDate(getState());

      const { data } = await recipeIngredientProductService.getRecipeIngredientProducts(
        recipeIngredientHeaderId,
        customerId,
        deliveryDate
      );

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredientProducts(data.ResultObject));
        dispatch(getInventoryEntryUsdaProductStatusTypesByProducts(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const createRecipeIngredientProduct =
  (
    request: CreateRecipeIngredientProductRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientProduct | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    try {
      request.DeliveryDate = selectNextDeliveryDate(getState());

      const { data } = await recipeIngredientProductService.createRecipeIngredientProduct(request);
      if (data.IsSuccess) {
        dispatch(upsertAppNotificationByKey(NotificationKeys.Toast, '', 'Item successfully linked to ingredient'));
        dispatch(
          ingredientsSlice.actions.setIngredientProduct({
            product: data.ResultObject,
            isCustomerPrimary: request.IsCustomerPrimary,
          })
        );
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.IngredientDetail,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const createRecipeIngredientProducts =
  (
    request: CreateRecipeIngredientProductsRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientProduct[] | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    const deliveryDate = selectNextDeliveryDate(getState());

    for (const singleRequest of request.Requests) {
      singleRequest.DeliveryDate = deliveryDate;
    }

    try {
      const { data } = await recipeIngredientProductService.createRecipeIngredientProducts(request);

      if (data.IsSuccess) {
        dispatch(
          upsertAppNotificationByKey(
            NotificationKeys.Toast,
            '',
            `${data.ResultObject.length} product(s) linked to ingredient`
          )
        );
        dispatch(ingredientsSlice.actions.setManyIngredientProducts(data.ResultObject));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.IngredientDetail,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }

    return result;
  };

export const updateRecipeIngredientProduct =
  (
    request: UpdateRecipeIngredientProductRequest,
    resolution: Resolution
  ): AppThunk<Promise<RecipeIngredientProduct | undefined>> =>
  async (dispatch, getState) => {
    let result = undefined;
    try {
      request.DeliveryDate = selectNextDeliveryDate(getState());

      const { data } = await recipeIngredientProductService.updateRecipeIngredientProduct(request);

      if (data.IsSuccess) {
        dispatch(
          ingredientsSlice.actions.setIngredientProduct({
            product: data.ResultObject,
            isCustomerPrimary: request.IsCustomerPrimary,
          })
        );
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.IngredientDetail,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
        dispatch(
          upsertAppNotification(
            {
              Id: generateStaticId('toastNotification', []),
              NotificationType: NotificationType.Success,
              NotificationDisplayType: NotificationDisplayType.Toast,
              AutoDismiss: 10,
              CanUserDismiss: true,
              Key: NotificationKeys.Toast,
              Message: 'Pricing UOM has been updated',
            },
            0
          )
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    return result;
  };

export const sequenceRecipeIngredientProducts =
  (request: SequenceRecipeIngredientProductsRequest, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    let result = false;
    try {
      dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(true));

      const { data } = await recipeIngredientProductService.sequenceRecipeIngredientProducts(request);

      if (data.IsSuccess) {
        await dispatch(getIngredientProducts(request.RecipeIngredientHeaderId, request.CustomerId));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.IngredientDetail,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.IsSuccess;
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
    dispatch(ingredientsSlice.actions.setIsIngredientProductsLoading(false));

    return result;
  };

export const deleteIngredientProduct =
  (request: DeleteRecipeIngredientProductRequest, resolution: Resolution): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const { data } = await recipeIngredientProductService.deleteRecipeIngredientProduct(request);
      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.removeIngredientProduct(request.RecipeIngredientProductId));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditIngredient,
            pageName: UserActivityPageName.IngredientDetail,
            actionSummary: UserActivityActionSummary.EditIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Ingredient diary
export const getIngredientDiaries =
  (customerId: string, ingredientId: string, startDate?: Date | string, endDate?: Date | string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientDiariesLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientDiariesLoading(true));
      }

      if (!endDate) {
        endDate = truncateTime(new Date());
        endDate = addDays(endDate, 1);
      }
      if (!startDate) {
        startDate = truncateTime(new Date());
        startDate = truncateTime(addDays(startDate, -7));
      }

      const { data } = await recipeIngredientDiaryService.getRecipeIngredientDiarys({
        CustomerId: customerId,
        IngredientId: ingredientId,
        StartDate: startDate,
        EndDate: endDate,
      });

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredientDiaries(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientDiariesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Ingredient customer diary
export const getIngredientCustomerDiaries =
  (customerId: string, startDate?: Date | string, endDate?: Date | string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientCustomerDiariesLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientCustomerDiariesLoading(true));
      }

      if (!endDate) {
        endDate = truncateTime(new Date());
        endDate = addDays(endDate, 1);
      }
      if (!startDate) {
        startDate = truncateTime(new Date());
        startDate = truncateTime(addDays(startDate, -7));
      }

      const { data } = await recipeIngredientDiaryService.getRecipeCustomerIngredientDiarys(
        customerId,
        startDate,
        endDate
      );

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredientCustomerDiaries(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientCustomerDiariesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Ingredient recipes
export const getIngredientRecipes =
  (customerId: string, ingredientId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().ingredients.isIngredientRecipesLoading) {
        dispatch(ingredientsSlice.actions.setIsIngredientRecipesLoading(true));
      }

      const { data } = await recipeDetailService.getRecipeDetailHeadersByIngredient(customerId, ingredientId);

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.setIngredientRecipes(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(ingredientsSlice.actions.setIsIngredientRecipesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const revertRecipeCustomerIngredientDiaryAction =
  (recipeCustomerIngredientDiaryId: string, resolution: Resolution): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await recipeIngredientDiaryService.revertRecipeCustomerIngredientDiaryAction({
        RecipeCustomerIngredientDiaryId: recipeCustomerIngredientDiaryId,
      });

      if (data.IsSuccess) {
        dispatch(ingredientsSlice.actions.removeRecipeIngredientCustomerDiary(recipeCustomerIngredientDiaryId));
        dispatch(ingredientsSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.RevertDeletedIngredient,
            pageName: UserActivityPageName.IngredientCustomerDiary,
            actionSummary: UserActivityActionSummary.RevertDeletedIngredient,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const getInventoryEntryUsdaProductStatusTypesByProducts =
  (products: RecipeIngredientProduct[]): AppThunk =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const customerId = getState().customer.selectedCustomer?.CustomerId ?? '';
    const ingredientProductKeys = products.filter((p) => p.Product?.IsUSDAProduct).map((rp) => rp.ProductKey);
    if (ingredientProductKeys.length === 0) return;

    dispatch(
      getUSDAProductStatusTypes(customerId, ingredientProductKeys, (statusResults) => {
        const productStatusTypeMap = statusResults.reduce<Record<string, number>>((map, statusResult) => {
          if (statusResult.ProductKey) {
            map[normalizeProductKey(statusResult.ProductKey)] = statusResult.USDAProductStatusType;
          }
          return map;
        }, {});

        dispatch(ingredientsSlice.actions.setUsdaProductStatusType(productStatusTypeMap));
      })
    );
  };
